import { API_BASE_URL, API_ENDPOINTS } from "../constants/api";
import Http from "../services/http";
import { AdssoVerifyRes } from "../types/auth";
import { ADSSO_TOKEN_EXPIRY } from "../constants/storage";
import { LocalStorage } from "../services/storage";

export const useAdssoLoginGuard = () => {
  const redirect_uri = `${API_BASE_URL}${API_ENDPOINTS.ADSSO_LOGIN_GUARD}`;

  const ssoLoginRedirect = () => {
    const nonce =
      Math.random().toString(36).substring(2, 15) +
      Math.random().toString(36).substring(2, 15);
    // Set nonce cookie for backend validation
    document.cookie = `AD_NONCE=${nonce}; Domain=.${new URL(API_BASE_URL || "").hostname}; Path=/; Secure; Max-Age=300`;
    window.location.href = `${process.env.REACT_APP_AD_SSO_BASE_URL}${process.env.REACT_APP_AD_SSO_AUTHORIZE_URL}?client_id=${process.env.REACT_APP_AD_SSO_CLIENT_ID}&scope=openid+profile&response_type=code+id_token&response_mode=form_post&redirect_uri=${encodeURIComponent(redirect_uri)}&nonce=${nonce}`;
  };

  /**
   * Asks the backend whether the ADSSO session is still valid, with caching.
   *
   * The token now lives in an HttpOnly cookie, so the frontend cannot read it
   * and has to ask. Anything other than an explicit `is_authenticated: true`
   * — a 403 for an expired token, a network failure, a malformed body — is
   * read as "not authenticated" so the caller falls back to the SSO redirect.
   *
   * Caches the token expiry time to avoid repeated API calls. Only verifies if:
   * - No cached expiry exists
   * - Cached expiry has passed
   */
  const verifyAdssoToken = async (): Promise<boolean> => {
    // Check if we have a cached, non-expired token.
    // Uses localStorage so the cache survives tab
    // closes and browser restarts — avoids a network round-trip on every
    // fresh hard reload / new tab.
    const cachedExpiry = LocalStorage.get(ADSSO_TOKEN_EXPIRY);
    if (cachedExpiry) {
      const expiryTime = new Date(cachedExpiry);
      // Leave a small buffer (5 minutes) before expiry to avoid race conditions
      const bufferMs = 5 * 60 * 1000;
      if (expiryTime.getTime() - bufferMs > Date.now()) {
        // Token is still valid, no need to call the API
        return true;
      }
      // Cached expiry has passed, fall through to verify
    }

    // Call the API to verify the token is still valid
    try {
      const res: AdssoVerifyRes = await Http.post(
        `${API_BASE_URL}${API_ENDPOINTS.ADSSO_LOGIN_VERIFY}`
      );

      if (res?.is_authenticated === true) {
        // Cache the expiry time for future calls
        if (res.token_expiry) {
          LocalStorage.set(ADSSO_TOKEN_EXPIRY, res.token_expiry);
        }
        return true;
      }

      // Not authenticated, clear cache
      LocalStorage.remove(ADSSO_TOKEN_EXPIRY);
      return false;
    } catch (e) {
      console.error("ADSSO verification failed", e);
      LocalStorage.remove(ADSSO_TOKEN_EXPIRY);
      return false;
    }
  };

  return { ssoLoginRedirect, verifyAdssoToken };
};
